/* eslint-disable react/prop-types */
import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    formatCount,
    formatShare,
    operatorColor,
    PAYMENT_THEME,
    statusTheme,
} from '../utils/theme';
import {
    activeSummary,
    groupByOperator,
    statusTotals,
} from '../utils/groupByOperator';
import { hasOperatorDetail, operatorDetailPath, PRODUCTS } from '../utils/navigation';
import { Meter, Swatch } from './Meters';
import StatusOverviewTable from './StatusOverviewTable';
import StatusRings from './StatusRings';
import styles from '../styles/Dashboard.module.css';

/** Una barra por operador, con su color de marca y el nombre siempre al lado. */
function ShareList({ items, total, label }) {
    return (
        <ul className={styles.shareList} aria-label={label}>
            {items.map((item) => (
                <li key={item.name} className={styles.meterRow}>
                    <span className={styles.meterName}>
                        <Swatch color={operatorColor(item.name)} />
                        {item.name}
                    </span>
                    <Meter
                        value={item.total}
                        total={total}
                        color={operatorColor(item.name)}
                        label={`${item.name}: ${formatCount(item.total)}`}
                    />
                    <span className={styles.meterValue}>
                        {formatCount(item.total)}
                        <span className={styles.meterShare}>
                            {formatShare(item.total, total)}
                        </span>
                    </span>
                </li>
            ))}
        </ul>
    );
}

/** Quién cobra: Apio, Stripe y las que no tienen cobro. */
function PaymentChips({ values, label, className }) {
    const segments = ['apio', 'stripe', 'ninguno']
        .map((key) => ({ key, value: values[key], ...PAYMENT_THEME[key] }))
        .filter((segment) => segment.value > 0);

    return (
        <ul className={className} aria-label={label}>
            {segments.map((segment) => (
                <li key={segment.key}>
                    <Swatch color={segment.color} isTrack={segment.isTrack} />
                    {segment.label}
                    <strong>{formatCount(segment.value)}</strong>
                </li>
            ))}
        </ul>
    );
}

function ActiveSummaryTile({ summary, parkTotal }) {
    return (
        <article className={`${styles.tile} ${styles.tileWide}`}>
            <p
                className={styles.parkFigure}
                aria-label={`${formatCount(summary.total)} suscripciones activas`}
            >
                <span className={styles.parkTotal}>{formatCount(summary.total)}</span>
                <span className={styles.parkUnit}>suscripciones activas</span>
            </p>
            <p className={styles.parkContext}>
                {formatShare(summary.total, parkTotal)} de las {formatCount(parkTotal)} del
                parque
            </p>

            {summary.byOperator.length > 0 && (
                <ShareList
                    items={summary.byOperator}
                    total={summary.total}
                    label="Activas por operador"
                />
            )}

            <PaymentChips
                values={summary}
                label="Cobro de las activas"
                className={`${styles.payment} ${styles.heroPayment}`}
            />
        </article>
    );
}

/**
 * Tarjeta por operador. Si el operador tiene página de detalle, el nombre es
 * un enlace que se estira a toda la tarjeta: se puede pulsar en cualquier
 * parte, pero el lector de pantalla anuncia solo el nombre.
 */
function OperatorCard({ operator, parkTotal, project }) {
    const linked = hasOperatorDetail(operator.name);

    return (
        <article
            className={
                linked
                    ? `${styles.tile} ${styles.operatorTile} ${styles.operatorTileLinked}`
                    : `${styles.tile} ${styles.operatorTile}`
            }
        >
            <header className={styles.operatorHead}>
                <h3 className={styles.operatorName}>
                    <span
                        className={styles.operatorDot}
                        style={{ background: operatorColor(operator.name) }}
                    />
                    {linked ? (
                        <Link
                            to={operatorDetailPath(project, operator.name)}
                            className={styles.operatorLink}
                        >
                            {operator.name}
                        </Link>
                    ) : (
                        operator.name
                    )}
                </h3>
                <p className={styles.operatorTotal}>{formatCount(operator.total)}</p>
            </header>
            <p className={styles.operatorMeta}>
                {formatShare(operator.total, parkTotal)} del parque
                {operator.total
                    ? `, ${formatShare(operator.activated, operator.total)} activas`
                    : ''}
            </p>

            <ul className={styles.statusRows}>
                {operator.statuses.map((item) => {
                    const status = statusTheme(item.status);
                    return (
                        <li key={item.status} className={styles.meterRow}>
                            <span className={styles.meterName}>{status.label}</span>
                            <Meter
                                value={item.total}
                                total={operator.total}
                                color={status.color}
                                label={`${status.label}: ${formatCount(item.total)}`}
                            />
                            <span className={styles.meterValue}>
                                {formatCount(item.total)}
                            </span>
                        </li>
                    );
                })}
            </ul>

            <PaymentChips
                values={operator}
                label={`Cobro de ${operator.name}`}
                className={styles.payment}
            />
        </article>
    );
}

export default function OperatorBoard({ project, projectData, loading }) {
    const operators = groupByOperator(projectData?.rows);
    const statuses = statusTotals(projectData?.rows);
    const active = activeSummary(projectData?.rows);
    const total = projectData?.total ?? 0;
    const projectKey = project || projectData?.project;
    const product = PRODUCTS[projectKey] || PRODUCTS.watch;
    const projectLabel = PRODUCTS[projectKey]?.label || projectKey || '…';
    // La tabla es para consultar un número puntual: cerrada por defecto.
    const [detailOpen, setDetailOpen] = useState(false);
    const detailId = useId();
    const generatedAt = projectData?.generatedAt
        ? new Date(projectData.generatedAt).toLocaleString('es-CL', {
              timeZone: 'America/Santiago',
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
          })
        : null;
    const metaText = (() => {
        if (loading && !projectData) return 'Cargando el parque';
        if (generatedAt) return `Actualizado ${generatedAt}`;
        return '';
    })();

    return (
        <section className={styles.product}>
            <header className={styles.productHead}>
                <div className={styles.productTitleRow}>
                    <img src={product.icon} alt="" className={styles.productIcon} />
                    <h2 className={styles.productTitle}>{projectLabel}</h2>
                </div>
                <p className={styles.meta}>
                    {metaText}
                    {projectData?.cached ? ' (cache)' : ''}
                </p>
            </header>

            {loading && operators.length === 0 ? (
                <div className={styles.bento} aria-hidden="true">
                    <div className={`${styles.tile} ${styles.tileWide} ${styles.skeleton}`} />
                    <div className={`${styles.tile} ${styles.skeleton}`} />
                </div>
            ) : operators.length === 0 ? (
                <p className={`${styles.tile} ${styles.empty}`}>
                    No hay suscripciones de operadores visibles para mostrar.
                </p>
            ) : (
                <>
                    <div className={styles.bento}>
                        <ActiveSummaryTile summary={active} parkTotal={total} />
                        <article className={styles.tile}>
                            <StatusRings
                                statuses={statuses}
                                total={total}
                                label={`Estados de ${projectLabel}`}
                            />
                        </article>
                    </div>

                    <div className={styles.operatorGrid}>
                        {operators.map((operator) => (
                            <OperatorCard
                                key={operator.name}
                                operator={operator}
                                parkTotal={total}
                                project={projectKey}
                            />
                        ))}
                    </div>

                    <article className={`${styles.tile} ${styles.detail}`}>
                        <h3 className={styles.detailHeading}>
                            <button
                                type="button"
                                className={styles.detailToggle}
                                aria-expanded={detailOpen}
                                aria-controls={detailId}
                                onClick={() => setDetailOpen((open) => !open)}
                            >
                                <span className={styles.detailTitle}>Detalle</span>
                                <span className={styles.detailAction}>
                                    {detailOpen ? 'Ocultar detalle' : 'Mostrar detalle'}
                                    <svg
                                        className={
                                            detailOpen
                                                ? `${styles.chevron} ${styles.chevronOpen}`
                                                : styles.chevron
                                        }
                                        viewBox="0 0 12 12"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M2.5 4.5 6 8l3.5-3.5"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.6"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                            </button>
                        </h3>
                        {detailOpen && (
                            <div id={detailId} className={styles.detailBody}>
                                <StatusOverviewTable
                                    rows={projectData?.rows}
                                    loading={loading && !projectData}
                                />
                            </div>
                        )}
                    </article>
                </>
            )}
        </section>
    );
}
